import { z } from "zod";

/**
 * Media type enum matching GraphQL MediaType
 */
export const MediaTypeEnum = z.enum(["image", "video", "audio", "file"]);

/**
 * Access level enum matching GraphQL AccessLevel
 */
export const AccessLevelEnum = z.enum(["public", "private", "shared_link"]);

/**
 * Folder creation validation schema (used by web and API)
 */
export const createFolderSchema = z.object({
  name: z
    .string()
    .min(1, "Folder name is required")
    .max(255, "Folder name is too long")
    .regex(/^[^/\\]+$/, "Folder name cannot contain slashes"),
  parentId: z.string().optional(),
  type: MediaTypeEnum,
});

/**
 * Folder rename validation schema (used by web and API)
 */
export const renameFolderSchema = z.object({
  id: z.string().min(1, "Folder ID is required"),
  name: z
    .string()
    .min(1, "Folder name is required")
    .max(255, "Folder name is too long")
    .regex(/^[^/\\]+$/, "Folder name cannot contain slashes"),
});

/**
 * Upload media validation schema (used by web)
 * Note: File validation happens separately via multipart upload
 */
export const uploadMediaSchema = z.object({
  folderId: z.string().min(1, "Folder ID is required"),
  audioText: z.string().optional(), // Transcription text for audio/video
  tags: z.array(z.string()).optional(), // Tags for categorization
  duration: z.number().int().positive().optional(), // Duration in seconds for audio/video
});

/**
 * Rename media validation schema (used by web and API)
 */
export const renameMediaSchema = z.object({
  id: z.string().min(1, "Media ID is required"),
  filename: z
    .string()
    .min(1, "Filename is required")
    .max(255, "Filename is too long")
    .regex(/^[^/\\]+$/, "Filename cannot contain slashes"),
});

/**
 * Move media validation schema (used by web and API)
 * targetFolderId can be null to move to root level
 */
export const moveMediaSchema = z.object({
  id: z.string().min(1, "Media ID is required"),
  targetFolderId: z.string().min(1, "Target folder ID is required").nullable(),
});

/**
 * Update media access level validation schema (used by web and API)
 */
export const updateMediaAccessLevelSchema = z.object({
  id: z.string().min(1, "Media ID is required"),
  accessLevel: AccessLevelEnum,
});

/**
 * Link media validation schema (used by web and API)
 */
export const linkMediaSchema = z.object({
  fromId: z.string().min(1, "Source media ID is required"),
  toId: z.string().min(1, "Target media ID is required"),
});

/**
 * Update media metadata validation schema (used by web and API)
 */
export const updateMediaMetadataSchema = z.object({
  id: z.string().min(1, "Media ID is required"),
  audioText: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

/**
 * File upload constraints (used by web for client-side validation)
 */
export const fileUploadConstraints = {
  maxSizeBytes: 100 * 1024 * 1024, // 100MB
  maxSizeMB: 100,
  allowedMimeTypes: {
    image: ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"],
    video: ["video/mp4", "video/webm", "video/quicktime"],
    audio: ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4"],
    file: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/zip",
      "text/plain",
    ],
  },
} as const;

/**
 * Phoneme type enum for phonics categorization
 * Must match GraphQL enum values exactly (using underscores)
 */
export const PhonemeTypeEnum = z.enum([
  "consonant",
  "short_vowel",
  "long_vowel",
  "digraph",
  "trigraph",
  "blend",
  "diphthong",
  "r_controlled",
  "vowel_team",
  "silent_letter",
  "variant_vowel",
  "schwa",
  "soft_hard",
  "special_pattern",
  "other",
]);

/**
 * Arpabet phone list for this phoneme, e.g. { sequence: "b|ah", targetIndex: 0 }.
 * targetIndex picks which phone in the sequence is actually scored.
 */
export const arpabetSchema = z
  .object({
    sequence: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z]{1,3}(\|[a-z]{1,3})*$/, "Sequence must be Arpabet codes separated by |"),
    targetIndex: z.number().int().min(0),
  })
  .superRefine((value, ctx) => {
    if (value.targetIndex >= value.sequence.split("|").length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetIndex"],
        message: "Target index is outside the sequence",
      });
    }
  });

/**
 * Reference text that makes Azure Pronunciation Assessment expect this sound, plus the IPA to look
 * for in what it derives. Stored per phoneme so a spelling can be corrected without a deploy, and
 * so two records sharing a grapheme (short-vowel "a" vs schwa "a") can differ.
 */
export const azureReferenceSchema = z.object({
  text: z.string().trim().min(1, "Reference text is required").max(30),
  targetIPA: z.array(z.string().trim().min(1)).min(1, "At least one target sound is required"),
});

/**
 * Create phoneme validation schema (used by web and API)
 */
export const createPhonemeSchema = z.object({
  phoneme: z
    .string()
    .min(1, "Phoneme is required")
    .max(20, "Phoneme is too long")
    .trim()
    .toLowerCase(),
  phonemeType: PhonemeTypeEnum,
  tags: z.array(z.string()).optional(),
  arpabet: arpabetSchema,
  azureReference: azureReferenceSchema.optional(),
  hint: z.string().trim().max(200).optional(),
});

/**
 * Update phoneme validation schema (used by web and API)
 */
export const updatePhonemeSchema = z.object({
  id: z.string().min(1, "Phoneme ID is required"),
  phoneme: z.string().min(1).max(20).trim().toLowerCase().optional(),
  phonemeType: PhonemeTypeEnum.optional(),
  tags: z.array(z.string()).optional(),
  arpabet: arpabetSchema.optional(),
  azureReference: azureReferenceSchema.optional(),
  hint: z.string().trim().max(200).optional(),
});

export const PhonicsAudioLabelEnum = z.enum(["card", "sound"]);

/**
 * Upload phonics audio validation schema (used by web and API)
 */
export const uploadPhonicsAudioSchema = z.object({
  phonemeId: z.string().min(1, "Phoneme ID is required"),
  voiceVariant: z.number().int().min(1, "Voice variant must be at least 1"),
  label: PhonicsAudioLabelEnum.default("card"),
  duration: z.number().int().positive().optional(),
  speaker: z.string().trim().max(60).optional(),
});

/**
 * Update phonics audio validation schema (used by web and API)
 */
export const updatePhonicsAudioSchema = z.object({
  id: z.string().min(1, "Phonics audio ID is required"),
  voiceVariant: z.number().int().min(1).optional(),
  duration: z.number().int().positive().optional(),
  label: PhonicsAudioLabelEnum.optional(),
  speaker: z.string().trim().max(60).optional(),
});

/**
 * Reorder phonics audio validation schema (used by web and API)
 * used to reorder voice variants for a phoneme via drag-and-drop
 */
export const reorderPhonicsAudioSchema = z.object({
  phonemeId: z.string().min(1, "Phoneme ID is required"),
  orderedIds: z.array(z.string().min(1)).min(1, "At least one ID is required"),
});

// Export inferred types
export type MediaType = z.infer<typeof MediaTypeEnum>;
export type AccessLevel = z.infer<typeof AccessLevelEnum>;
export type PhonemeType = z.infer<typeof PhonemeTypeEnum>;
export type CreateFolderInput = z.infer<typeof createFolderSchema>;
export type RenameFolderInput = z.infer<typeof renameFolderSchema>;
export type UploadMediaInput = z.infer<typeof uploadMediaSchema>;
export type RenameMediaInput = z.infer<typeof renameMediaSchema>;
export type MoveMediaInput = z.infer<typeof moveMediaSchema>;
export type UpdateMediaAccessLevelInput = z.infer<typeof updateMediaAccessLevelSchema>;
export type LinkMediaInput = z.infer<typeof linkMediaSchema>;
export type UpdateMediaMetadataInput = z.infer<typeof updateMediaMetadataSchema>;
export type Arpabet = z.infer<typeof arpabetSchema>;
export type AzureReference = z.infer<typeof azureReferenceSchema>;
export type CreatePhonemeInput = z.infer<typeof createPhonemeSchema>;
export type UpdatePhonemeInput = z.infer<typeof updatePhonemeSchema>;
export type PhonicsAudioLabel = z.infer<typeof PhonicsAudioLabelEnum>;
export type UploadPhonicsAudioInput = z.infer<typeof uploadPhonicsAudioSchema>;
export type UpdatePhonicsAudioInput = z.infer<typeof updatePhonicsAudioSchema>;
export type ReorderPhonicsAudioInput = z.infer<typeof reorderPhonicsAudioSchema>;
